import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import config from '../config';
import { usePrevious } from '../hooks/useHasChanged';
import { useAuth } from './AuthProvider';

const wsURL = config.wsAdress;

//---------------------------------------------------

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType | null>(null);

interface SocketProviderProps {
  children: React.ReactNode[] | React.ReactNode;
}

const SocketProvider = ({ children }: SocketProviderProps) => {
  const [socket, setSocket] = useState<any | null>(null);

  const { account, isAuthentificated } = useAuth();
  const { _id } = account || {};

  const prevIsAuthentificated = usePrevious(isAuthentificated);

  useEffect(() => {
    const socketInstance = io(wsURL);

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  useEffect(() => {
    if (socket?.connected && _id && isAuthentificated) {
      socket.emit('userConnected', _id);
    }
  }, [socket, _id, isAuthentificated]);

  useEffect(() => {
    if (socket?.connected && !isAuthentificated && prevIsAuthentificated) {
      socket.emit('userDisconnected');
    }
  }, [socket, isAuthentificated, prevIsAuthentificated]);

  const value = useMemo(
    () => ({
      socket,
    }),
    [socket]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }

  return context;
};

export default SocketProvider;
